package link.erpango.quip.domain.noteimport

import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import link.erpango.quip.domain.extraction.ExtractionConfidence
import link.erpango.quip.domain.extraction.normalizeText

data class NormalizedDate(
    val date: LocalDate,
    val precision: DatePrecision,
    val confidence: ExtractionConfidence,
)

private const val MILLIS_PER_DAY = 86_400_000L

private val DAY_FIRST_REGEX = Regex("^(\\d{1,2})[/.\\-](\\d{1,2})[/.\\-](\\d{4})$")
private val ISO_REGEX = Regex("^(\\d{4})-(\\d{1,2})-(\\d{1,2})$")
private val MONTH_REGEX = Regex(
    "^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|setiembre|octubre|noviembre|diciembre)" +
        "(?:\\s+(?:de(?:l)?\\s+)?((?:19|20)\\d{2}))?$"
)

private val MONTHS = mapOf(
    "enero" to 1, "febrero" to 2, "marzo" to 3, "abril" to 4, "mayo" to 5, "junio" to 6,
    "julio" to 7, "agosto" to 8, "septiembre" to 9, "setiembre" to 9, "octubre" to 10,
    "noviembre" to 11, "diciembre" to 12,
)

/**
 * Turns a date as written in a note into a calendar date.
 *
 * - "12/03/2025", "12-3-2025", "2025-03-12" -> that day ([DatePrecision.DAY], HIGH)
 * - "marzo 2025", "marzo de 2025" -> the 1st of that month ([DatePrecision.MONTH], MEDIUM)
 * - "marzo" -> the 1st of the most recent March not after [today] ([DatePrecision.MONTH], LOW)
 *
 * Returns null for anything else (a bare year is too vague to prefill a date) and for
 * impossible dates such as 31/02/2025. Numeric dates are read day-first (Spanish notes).
 */
fun normalizeNoteDate(raw: String, today: LocalDate): NormalizedDate? {
    val text = normalizeText(raw.trim()).replace(Regex("\\s+"), " ")

    DAY_FIRST_REGEX.matchEntire(text)?.let { match ->
        val (day, month, year) = match.destructured
        return dayDate(year.toInt(), month.toInt(), day.toInt(), today)
    }
    ISO_REGEX.matchEntire(text)?.let { match ->
        val (year, month, day) = match.destructured
        return dayDate(year.toInt(), month.toInt(), day.toInt(), today)
    }
    MONTH_REGEX.matchEntire(text)?.let { match ->
        val month = MONTHS.getValue(match.groupValues[1])
        val yearText = match.groupValues[2]
        return if (yearText.isNotEmpty()) {
            safeDate(yearText.toInt(), month, 1, today)
                ?.let { NormalizedDate(it, DatePrecision.MONTH, ExtractionConfidence.MEDIUM) }
        } else {
            val year = if (month <= today.month.number) today.year else today.year - 1
            safeDate(year, month, 1, today)
                ?.let { NormalizedDate(it, DatePrecision.MONTH, ExtractionConfidence.LOW) }
        }
    }
    return null
}

private fun dayDate(year: Int, month: Int, day: Int, today: LocalDate): NormalizedDate? =
    safeDate(year, month, day, today)?.let { NormalizedDate(it, DatePrecision.DAY, ExtractionConfidence.HIGH) }

/** A valid date between 1900 and next year, or null. */
private fun safeDate(year: Int, month: Int, day: Int, today: LocalDate): LocalDate? {
    if (year < 1900 || year > today.year + 1) return null
    return try {
        LocalDate(year, month, day)
    } catch (_: IllegalArgumentException) {
        null
    }
}

/** Epoch millis at UTC midnight of this date (what the Material date picker returns). */
fun LocalDate.toUtcMidnightMillis(): Long = toEpochDays() * MILLIS_PER_DAY

/** Inverse of [toUtcMidnightMillis]. */
fun localDateFromUtcMillis(millis: Long): LocalDate =
    LocalDate.fromEpochDays(millis.floorDiv(MILLIS_PER_DAY))
